import type {
  BlockStateDefinition,
  ComponentType,
  ImplicitDependencyGroup,
} from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type {
  ImageGridBlockStateType,
  ImageGridComponentType,
} from '@unifyapps/defs/blocks/ImageGrid/types';
import type { Action } from '@unifyapps/defs/types/action';
import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import { lazy } from 'react';
import { BlockHelper } from '@unifyapps/carbon/helpers/BlockHelper';
import { DependencyPaths } from '@unifyapps/carbon/no-code/helper/DependencyPaths';

const importImageGridComponent = () =>
  import(
    /* webpackChunkName: "image-grid-block" */
    './ImageGrid'
  ) as Promise<{ default: ComponentType }>;

const ImageGrid = lazy(importImageGridComponent) as ComponentType;

class ImageGridBlockStateDefinition implements BlockStateDefinition {
  getComponent() {
    return ImageGrid;
  }

  get type(): ComponentTypeUnionType {
    return 'ImageGrid';
  }

  getBlockControlMethods(block: BlockType<ImageGridComponentType>) {
    if (!block.component.content.addOns?.select?.enabled) {
      return [];
    }

    return [
      {
        id: 'selectImage',
        label: 'Select a Image',
        method: (_blockState: BlockStateUnionType, action: Action) => {
          const blockState = _blockState as ImageGridBlockStateType;

          const { methodPayload } = action.payload as {
            methodPayload?: { imageId: string };
          };

          if (!methodPayload?.imageId) return blockState as BlockStateUnionType;

          if (blockState.selectedImages.type === 'SELECT_ALL')
            return blockState as BlockStateUnionType;

          if (
            blockState.selectedImages.type === 'INCLUDE' &&
            blockState.selectedImages.ids?.includes(methodPayload.imageId)
          ) {
            return blockState as BlockStateUnionType;
          }

          if (
            blockState.selectedImages.type === 'EXCLUDE' &&
            !blockState.selectedImages.ids?.includes(methodPayload.imageId)
          ) {
            return blockState as BlockStateUnionType;
          }

          if (blockState.selectedImages.type === 'INCLUDE') {
            return {
              ...blockState,
              selectedImages: {
                ...blockState.selectedImages,
                ids: [...(blockState.selectedImages.ids || []), methodPayload.imageId],
              },
            } as BlockStateUnionType;
          }
          return {
            ...blockState,
            selectedImages: {
              ...blockState.selectedImages,
              ids:
                blockState.selectedImages.ids?.filter((id) => id !== methodPayload.imageId) || [],
            },
          } as BlockStateUnionType;
        },
      },
      {
        id: 'deselectImage',
        label: 'Deselect a Image',
        method: (_blockState: BlockStateUnionType, action: Action) => {
          const blockState = _blockState as ImageGridBlockStateType;
          const { methodPayload } = action.payload as {
            methodPayload?: { imageId: string };
          };

          if (!methodPayload?.imageId) return blockState as BlockStateUnionType;

          if (blockState.selectedImages.type === 'SELECT_ALL') {
            return {
              ...blockState,
              selectedImages: {
                type: 'EXCLUDE',
                ids: [methodPayload.imageId],
              },
            } as BlockStateUnionType;
          }

          if (blockState.selectedImages.type === 'INCLUDE') {
            const filteredIds =
              blockState.selectedImages.ids?.filter((id) => id !== methodPayload.imageId) || [];

            return {
              ...blockState,
              selectedImages: {
                type: 'INCLUDE',
                ids: filteredIds,
              },
            } as BlockStateUnionType;
          }

          return {
            ...blockState,
            selectedImages: {
              type: 'EXCLUDE',
              ids: [
                ...(blockState.selectedImages.ids?.filter((id) => id !== methodPayload.imageId) ||
                  []),
                methodPayload.imageId,
              ],
            },
          } as BlockStateUnionType;
        },
      },
      {
        id: 'selectAllImage',
        label: 'Select all images',
        method: (blockState: BlockStateUnionType) => {
          return {
            ...blockState,
            selectedImages: {
              type: 'SELECT_ALL',
            },
          } as BlockStateUnionType;
        },
      },
      {
        id: 'deselectAllImage',
        label: 'Deselect all images',
        method: (blockState: BlockStateUnionType) => {
          return {
            ...blockState,
            selectedImages: {
              type: 'INCLUDE',
              ids: [],
            },
          } as BlockStateUnionType;
        },
      },
    ];
  }

  get initialStateGetter() {
    return (block: BlockType) => {
      const component = block.component as ImageGridComponentType;
      return {
        id: block.id,
        selectedImages: {
          type: 'INCLUDE' as const,
          ids: [],
        },
        pagination: component.content.addOns?.page
          ? {
              currentPage: component.content.addOns.page.current,
              offset: component.content.addOns.page.offset,
              pageSize: component.content.addOns.page.size,
              total: component.content.addOns.page.total,
            }
          : undefined,
        ..._pick(block.component as ImageGridComponentType, [
          'content',
          'appearance',
          'componentType',
        ]),
      };
    };
  }

  getImplicitDependencyGroups(): ImplicitDependencyGroup {
    return { usingDataSourcesResponse: true };
  }

  getImplicitDependencies(block: BlockType<ImageGridComponentType>) {
    const dataSourceIds = BlockHelper.getDataSourceIds(block);
    const dataSourceId = dataSourceIds.find((id) =>
      block.component.content.mappedImageItems?.source?.includes(id),
    );
    if (!dataSourceId) return;

    const paths = DependencyPaths.dataSource(dataSourceId);

    return [
      {
        id: dataSourceId,
        p: [paths.hasNextPage, paths.isFetchingNextPage],
      },
    ];
  }
}

export default ImageGridBlockStateDefinition;
